/**
 * GlobalSearch — 상단 통합검색 (인라인 Combobox)
 *
 * 동작(목업 20260720 정합):
 * - 클릭/포커스 시 팝업 없이 인풋에 커서 → 타이핑하면 인풋 바로 아래 드롭다운으로 결과.
 * - ⌘K/Ctrl+K → 인풋 포커스(+드롭다운 오픈). 배경 스크림·블러 없음.
 * - 단축키 힌트 배지는 OS별 동적: macOS/iOS → ⌘K, 그 외 → Ctrl K.
 *   (키 바인딩은 Mantine `mod`가 Mac ⌘ / Win Ctrl 자동 매핑 — 배지 텍스트만 OS 분기.)
 *
 * 동적 메타모델 원칙(절대):
 * - 모듈명 분기 금지. 결과(SearchHit)의 typeCode/itemId 메타로 동적 라우팅
 *   (`/:workspace/m/:typeCode?item=:itemId` — 목록+우측 드로어). 모듈별 결과 타입 하드코딩 없음.
 * - 결과 그룹 라벨 = typeLabel/typeLabelI18n + resolveLabel. 표시 문자열 i18n.
 * - 엔드포인트 부재 시 useSearch가 빈 결과(graceful).
 *
 * 검색박스 전체(인풋+배지+드롭다운)를 소유함 — AppShell은 topbar 슬롯에 이 컴포넌트만 렌더.
 */
import { itemTitle } from "@/lib/dynamic/itemTitle";
import { useMemo, useRef, useState } from "react";
import { Badge, Combobox, Group, Loader, ScrollArea, Text, useCombobox } from "@mantine/core";
import { useDebouncedValue, useHotkeys, useOs } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { resolveLabel } from "@/lib/i18n/resolveLabel";
import { useLang } from "@/lib/i18n/useLang";
import { useSearch, type SearchHit } from "@/lib/queries/useSearch";

export function GlobalSearch() {
  const { t } = useTranslation();
  const { lang } = useLang();
  const navigate = useNavigate();
  const { workspace: slug } = useParams<{ workspace: string }>();
  // SPA(SSR 없음) → 첫 렌더부터 실제 OS 계산(배지 '⌘K'↔'Ctrl K' 깜빡임 방지)
  const os = useOs({ getValueInEffect: false });
  const inputRef = useRef<HTMLInputElement>(null);

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [query, setQuery] = useState("");
  const [debounced] = useDebouncedValue(query, 250);

  const { data: serverHits, isFetching } = useSearch(debounced, slug);
  const hits = useMemo(() => serverHits ?? [], [serverHits]);

  // onOptionSubmit(id) → hit 역참조
  const byId = useMemo(() => {
    const m = new Map<string, SearchHit>();
    for (const h of hits) m.set(h.id, h);
    return m;
  }, [hits]);

  // typeCode 그룹핑
  const grouped = useMemo(() => {
    const m = new Map<string, SearchHit[]>();
    for (const h of hits) {
      const arr = m.get(h.typeCode) ?? [];
      arr.push(h);
      m.set(h.typeCode, arr);
    }
    return m;
  }, [hits]);

  // ⌘K / Ctrl+K → 인풋 포커스(+드롭다운 오픈)
  useHotkeys([
    [
      "mod+K",
      () => {
        inputRef.current?.focus();
        combobox.openDropdown();
      },
    ],
  ]);

  const goToHit = (hit: SearchHit) => {
    // 전체 상세페이지(ItemDetailPage)가 아니라, 해당 업무유형 목록 + 우측 드로어로 진입(앱 표준 패턴).
    // WorkitemTypePage가 ?item=id 를 읽어 타입별 드로어(Doc/Capa/Change/…)를 열고, 사이드바는 해당 모듈에 포커스됨.
    if (slug)
      navigate(`/${slug}/w/${hit.typeCode.toLowerCase()}?item=${encodeURIComponent(hit.id)}`);
    setQuery("");
    combobox.closeDropdown();
    inputRef.current?.blur();
  };

  const kbdLabel = os === "macos" || os === "ios" ? "⌘K" : "Ctrl K";
  const trimmed = debounced.trim();

  return (
    <Combobox
      store={combobox}
      width="target"
      position="bottom-start"
      onOptionSubmit={(id) => {
        const hit = byId.get(id);
        if (hit) goToHit(hit);
      }}
    >
      <Combobox.DropdownTarget>
        <div className="tb-search">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <Combobox.EventsTarget>
            <input
              ref={inputRef}
              value={query}
              placeholder={t("search.globalPlaceholder", "문서·CAPA·부적합·공급자 통합검색")}
              aria-label={t("search.global", "통합검색")}
              onChange={(e) => {
                setQuery(e.currentTarget.value);
                combobox.openDropdown();
                combobox.updateSelectedOptionIndex();
              }}
              onFocus={() => combobox.openDropdown()}
              onBlur={() => combobox.closeDropdown()}
            />
          </Combobox.EventsTarget>
          {isFetching ? <Loader size="xs" /> : <kbd>{kbdLabel}</kbd>}
        </div>
      </Combobox.DropdownTarget>

      <Combobox.Dropdown hidden={trimmed.length === 0}>
        <Combobox.Options>
          <ScrollArea.Autosize mah="50vh" type="hover">
            {hits.length === 0 && !isFetching ? (
              <Combobox.Empty>{t("search.noResults", "검색 결과가 없습니다")}</Combobox.Empty>
            ) : (
              Array.from(grouped.entries()).map(([typeCode, group]) => {
                const first = group[0];
                const groupLabel = resolveLabel(
                  first.typeLabel ?? typeCode,
                  first.typeLabelI18n,
                  lang,
                );
                return (
                  <Combobox.Group label={groupLabel} key={typeCode}>
                    {group.map((hit) => {
                      // 스니펫 표시 판정: 본문(values) 매칭 시 BE가 JSONB 원문을 반환하므로
                      // raw JSON(`{`/`[` 시작) 숨김 + 공백-only·제목중복 스니펫도 숨겨 제목만 노출.
                      const snip = hit.snippet?.trim();
                      const showSnippet =
                        !!snip && !/^[[{]/.test(snip) && snip !== hit.title.trim();
                      return (
                        <Combobox.Option value={hit.id} key={hit.id}>
                          <Group gap="xs" wrap="nowrap">
                            {hit.itemNo && (
                              <Badge variant="light" size="sm">
                                {hit.itemNo}
                              </Badge>
                            )}
                            <Text size="sm" lineClamp={1} flex={1}>
                              {itemTitle(hit, lang)}
                            </Text>
                          </Group>
                          {showSnippet && (
                            <Text size="xs" c="dimmed" lineClamp={1}>
                              {hit.snippet}
                            </Text>
                          )}
                        </Combobox.Option>
                      );
                    })}
                  </Combobox.Group>
                );
              })
            )}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
